/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { debounceTask } from 'ember-lifeline';
import { isPresent } from '@ember/utils';
import UserModel from 'uplisting-frontend/models/user';
import BookingModel from 'uplisting-frontend/models/booking';
import ExtraPriceModel from 'uplisting-frontend/models/extra-price';
import { formatDateQuery, isSaveDisabled } from 'uplisting-frontend/utils';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/base';
import { type IExtraPriceSchema } from 'uplisting-frontend/models/schemas';

interface IArgs {
  booking: BookingModel;
}

export interface BookingPriceSignature {
  Element: HTMLDivElement;

  Args: IArgs;

  Blocks: {
    default: [];
  };
}

export default class UiBookingPriceComponent extends Component<BookingPriceSignature> {
  @service store!: Services['store'];
  @service session!: Services['session'];
  @service notifications!: Services['notifications'];

  @service('repositories/booking')
  bookingRepository!: Services['repositories/booking'];

  numberOfGuestsOptions = Array.from(Array(30), (_, i) => i + 1);

  @cached @tracked isUpdatingPrice = false;

  @cached
  get booking(): BookingModel {
    return this.args.booking;
  }

  @cached
  get currentUser(): UserModel {
    return this.session.currentUser as UserModel;
  }

  @cached
  get isEditable(): boolean {
    return (
      this.booking.isEditable &&
      this.currentUser.hasAccessTo('bookings.price.edit')
    );
  }

  @cached
  get isPricePermissible(): boolean {
    return this.currentUser.hasAccessTo('bookings.price.visible');
  }

  @cached
  get changeset(): GenericChangeset<BookingModel> {
    return this.bookingRepository.buildChangeset(this.booking);
  }

  @action
  handleDatesSelect(date1: Date, date2: Date): void {
    this.changeset.checkIn = formatDateQuery(date1);
    this.changeset.checkOut = formatDateQuery(date2);

    debounceTask(this, 'syncPrice', 250);
  }

  @action
  handleNumberOfGuestsChange(numberOfGuests: number): void {
    this.changeset.price.numberOfGuests = numberOfGuests;

    debounceTask(this, 'syncPrice', 250);
  }

  @action
  async handleFormSubmit(): Promise<void> {
    const { changeset } = this;

    const saveDisabled = await isSaveDisabled(changeset);

    if (saveDisabled) {
      return;
    }

    const numberOfGuestsChanged = changeset.changes.find(
      (change) => change['key'] === 'price.numberOfGuests',
    );

    if (numberOfGuestsChanged) {
      changeset.price.hostEdited = true;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const promises: Promise<any>[] = [];

      promises.push(changeset.save());

      if (numberOfGuestsChanged) {
        promises.push(this.booking.price.save());
      }

      await Promise.all(promises);

      this.notifications.info();
    } catch {
      this.notifications.error();
    }
  }

  private async syncPrice(): Promise<void> {
    const { changeset, booking } = this;
    const { price } = changeset;

    this.isUpdatingPrice = true;

    const charges = price.charges.map((item) => item.toObject());
    const discounts = price.discounts.map((item) => item.toObject());

    const quote = this.store.createRecord('quote', {
      booking,
      charges,
      discounts,
      property: booking.property,
      checkIn: changeset.checkIn,
      checkOut: changeset.checkOut,
      cleaningFee: price.cleaningFee,
      numberOfGuests: price.numberOfGuests,
    });

    try {
      await quote.save();

      Object.assign(changeset.price, quote.toObject());

      this.unloadRecords(price.charges);
      this.unloadRecords(price.discounts);

      changeset.price.charges = this.toExtraPrices(quote.charges);
      changeset.price.discounts = this.toExtraPrices(quote.discounts);

      changeset.execute();

      this.store.unloadRecord(quote);
    } finally {
      this.isUpdatingPrice = false;
    }
  }

  private toExtraPrices(items: IExtraPriceSchema[]): ExtraPriceModel[] {
    return items
      .filter((item) => isPresent(item.amount))
      .map((item) => this.store.createRecord('extra-price', item));
  }

  private unloadRecords(records: ExtraPriceModel[]): void {
    records.forEach((record) => {
      this.store.unloadRecord(record);
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Price': typeof UiBookingPriceComponent;
  }
}
